// admin-panel/src/pages/Dashboard.jsx
import { useQuery } from '@tanstack/react-query';
import { dashboardApi, inquiriesApi } from '../lib/api';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { MessageSquare, FileText, FolderOpen, TrendingUp,
  Users, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const COLORS = ['#D4AF37', '#8B5E3C', '#4A90D9', '#56C596', '#E87040'];

function StatCard({ label, value, sub, icon: Icon, color = '#D4AF37', trend }) {
  return (
    <div className="bg-[#151515] border border-white/5 rounded-xl p-5 hover:border-white/10 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center`}
          style={{ background: `${color}15` }}>
          <Icon size={18} style={{ color }} />
        </div>
        {trend !== undefined && (
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${trend >= 0 ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
            {trend >= 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div className="text-2xl font-bold text-white mb-1">{value}</div>
      <div className="text-xs text-[#666]">{label}</div>
      {sub && <div className="text-xs text-[#444] mt-0.5">{sub}</div>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-xs">
      <div className="text-[#888] mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }} className="font-medium">
          {p.name}: {p.name === 'Revenue' ? '₹' + p.value?.toLocaleString() : p.value}
        </div>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { data: stats } = useQuery({ queryKey: ['dashboard-stats'], queryFn: dashboardApi.stats });
  const { data: charts } = useQuery({ queryKey: ['dashboard-charts'], queryFn: dashboardApi.charts });
  const { data: recent } = useQuery({ queryKey: ['recent-inquiries'], queryFn: dashboardApi.recentInquiries });

  // Format chart data
  const inquiryChartData = charts?.inquiries_by_month?.map(d => ({
    month: d.month,
    Inquiries: d.count,
  })) || [];

  const revenueChartData = charts?.revenue_by_month?.map(d => ({
    month: d.month,
    Revenue: parseFloat(d.revenue || 0),
  })) || [];

  const mergedChartData = inquiryChartData.map((d, i) => ({
    ...d,
    Revenue: revenueChartData[i]?.Revenue || 0,
  }));

  const statusData = charts?.status_distribution?.map(d => ({
    name: d.status.replace('_', ' '),
    value: d.count,
  })) || [];

  const statusColors = {
    'new': '#D4AF37', 'contacted': '#4A90D9', 'in progress': '#56C596',
    'quoted': '#8B5E3C', 'converted': '#22c55e', 'closed': '#666', 'spam': '#ef4444'
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-white">Dashboard</h1>
        <p className="text-sm text-[#555] mt-0.5">Welcome back! Here's what's happening.</p>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Inquiries" value={stats?.inquiries?.total ?? '—'}
          sub={`${stats?.inquiries?.today ?? 0} today`} icon={MessageSquare} color="#4A90D9" trend={12} />
        <StatCard label="New / Unread" value={stats?.inquiries?.unread ?? '—'}
          sub={`${stats?.inquiries?.new ?? 0} new status`} icon={AlertCircle} color="#D4AF37" />
        <StatCard label="Accepted Quotes" value={stats?.quotes?.accepted ?? '—'}
          sub={`₹${(stats?.quotes?.total_value || 0).toLocaleString()} total value`} icon={FileText} color="#56C596" trend={8} />
        <StatCard label="Published Projects" value={stats?.projects?.published ?? '—'}
          sub={`${stats?.projects?.featured ?? 0} featured`} icon={FolderOpen} color="#8B5E3C" />
      </div>

      {/* ── Secondary Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="This Month" value={stats?.inquiries?.this_month ?? '—'}
          sub="inquiries" icon={TrendingUp} color="#D4AF37" />
        <StatCard label="This Week" value={stats?.inquiries?.this_week ?? '—'}
          sub="inquiries" icon={Clock} color="#4A90D9" />
        <StatCard label="Conversion Rate" value={`${stats?.conversion_rate ?? 0}%`}
          sub="inquiry to project" icon={CheckCircle} color="#56C596" />
        <StatCard label="Monthly Revenue" value={`₹${(stats?.quotes?.this_month_val || 0).toLocaleString()}`}
          sub="accepted quotes" icon={TrendingUp} color="#8B5E3C" />
      </div>

      {/* ── Charts Row ── */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Chart */}
        <div className="lg:col-span-2 bg-[#151515] border border-white/5 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white mb-4">Inquiries & Revenue (12 months)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={mergedChartData}>
              <defs>
                <linearGradient id="inquiryGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#D4AF37" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
              <XAxis dataKey="month" tick={{ fill: '#666', fontSize: 11 }} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fill: '#666', fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: '#666', fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area yAxisId="left" type="monotone" dataKey="Inquiries" stroke="#D4AF37" fill="url(#inquiryGrad)" strokeWidth={2} />
              <Bar yAxisId="right" dataKey="Revenue" fill="#8B5E3C" opacity={0.7} radius={[3,3,0,0]} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart */}
        <div className="bg-[#151515] border border-white/5 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white mb-4">Inquiry Status</h3>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                {statusData.map((entry, i) => (
                  <Cell key={i} fill={statusColors[entry.name] || COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 mt-2">
            {statusData.slice(0, 5).map((d, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: statusColors[d.name] || COLORS[i % COLORS.length] }} />
                  <span className="text-[#888] capitalize">{d.name}</span>
                </div>
                <span className="text-white font-medium">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Recent Inquiries ── */}
      <div className="bg-[#151515] border border-white/5 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">Recent Inquiries</h3>
          <Link to="/inquiries" className="text-xs text-[#D4AF37] hover:text-[#E8C84A] transition-colors">View all →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5">
                {['Name', 'Phone', 'Service', 'Status', 'Time'].map(h => (
                  <th key={h} className="text-left text-xs text-[#555] font-medium px-5 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent?.map((inq) => (
                <tr key={inq.id} className={`border-b border-white/5 hover:bg-white/3 transition-colors ${!inq.is_read ? 'bg-[#D4AF37]/3' : ''}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      {!inq.is_read && <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />}
                      <Link to={`/inquiries/${inq.id}`} className="text-white hover:text-[#D4AF37] font-medium transition-colors">
                        {inq.name}
                      </Link>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-[#888]">{inq.phone}</td>
                  <td className="px-5 py-3 text-[#888]">{inq.service || '—'}</td>
                  <td className="px-5 py-3">
                    <StatusBadge status={inq.status} />
                  </td>
                  <td className="px-5 py-3 text-[#555] text-xs">{inq.time}</td>
                </tr>
              )) || (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-[#555] text-xs">Loading...</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    new:         'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    contacted:   'bg-blue-500/10 text-blue-400 border-blue-500/20',
    in_progress: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    quoted:      'bg-orange-500/10 text-orange-400 border-orange-500/20',
    converted:   'bg-green-500/10 text-green-400 border-green-500/20',
    closed:      'bg-gray-500/10 text-gray-400 border-gray-500/20',
    spam:        'bg-red-500/10 text-red-400 border-red-500/20',
    draft:       'bg-gray-500/10 text-gray-400 border-gray-500/20',
    sent:        'bg-blue-500/10 text-blue-400 border-blue-500/20',
    accepted:    'bg-green-500/10 text-green-400 border-green-500/20',
    rejected:    'bg-red-500/10 text-red-400 border-red-500/20',
    published:   'bg-green-500/10 text-green-400 border-green-500/20',
    archived:    'bg-gray-500/10 text-gray-400 border-gray-500/20',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${map[status] || 'bg-gray-500/10 text-gray-400 border-gray-500/20'}`}>
      {status?.replace('_', ' ')}
    </span>
  );
}
